import { palette } from '@kimuno/theme';
import { IconButton, type IconButtonProps, SvgIcon, Tooltip } from '@mui/material';
import type React from 'react';
import { forwardRef } from 'react';

import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';

export type AIAssistantIconButtonProps = {
	icon: IconName;
	tooltip?: string;
	iconSize?: 'medium' | 'large';
	// Icons drawn on a canvas other than the rendered size would be cropped by the
	// box below — those pass their own (e.g. stopCircle is authored at 35x35).
	iconViewBox?: string;
} & IconButtonProps;

const AIAssistantIconButton: React.FC<AIAssistantIconButtonProps> = forwardRef(
	({ icon, tooltip, iconSize = 'medium', iconViewBox, ...props }, ref) => {
		const iconSizePx = iconSize === 'medium' ? 24 : 32;

		const buttonStyles = {
			color: palette.text.secondary,
			'&:hover': {
				backgroundColor: palette.primary.hover,
				color: palette.primary.main,
			},
			...props.sx,
		};

		const button = (
			<IconButton
				{...props}
				sx={buttonStyles}
				ref={ref}
				type={props.type || 'button'}
				aria-label={props['aria-label'] || tooltip || undefined}
			>
				<SvgIcon
					sx={{ width: iconSizePx, height: iconSizePx }}
					viewBox={iconViewBox || `0 0 ${iconSizePx} ${iconSizePx}`}
					component={getMuiIcon(icon)}
					aria-hidden="true"
				/>
			</IconButton>
		);

		return tooltip ? (
			<Tooltip title={tooltip} placement="top" arrow>
				{button}
			</Tooltip>
		) : (
			button
		);
	}
);

export default AIAssistantIconButton;
